import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { cn } from "@/lib/cn";

/**
 * An assistant answer, rendered as GFM markdown.
 *
 * Raw HTML is not enabled — `react-markdown` ignores it unless `rehype-raw` is
 * added, so model output cannot inject markup into the page. Every element is
 * styled from tokens rather than a typography plugin, because a rebrand has to
 * reach the answer text too (PRD FR-11).
 *
 * **Images are rendered as their caption, never as an image.** Local models
 * write `![Chart](https://…/placeholder.png)` into answers routinely — they are
 * describing the chart that is already beside them as an artifact. Rendering it
 * would put a broken glyph in the middle of an answer, and would turn a URL the
 * *model* chose into a request the reader's browser makes. The deployment's CSP
 * pins `img-src` to `self`, so nothing leaves; this is the second layer, and it
 * is the one that keeps holding when the same markdown is printed to a PDF by a
 * headless browser in M6.
 */
export function Markdown({ children, className }: { children: string; className?: string }) {
  return (
    <div className={cn("type-body text-ink flex flex-col gap-3", className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children }) => <p className="text-pretty">{children}</p>,
          h1: ({ children }) => <h3 className="type-h3 text-ink mt-1">{children}</h3>,
          h2: ({ children }) => <h4 className="type-h4 text-ink mt-1">{children}</h4>,
          h3: ({ children }) => <h4 className="type-h4 text-ink mt-1">{children}</h4>,
          strong: ({ children }) => <strong className="text-ink font-semibold">{children}</strong>,
          ul: ({ children }) => (
            <ul className="flex list-disc flex-col gap-1.5 ps-5">{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className="flex list-decimal flex-col gap-1.5 ps-5">{children}</ol>
          ),
          a: ({ children, href }) => (
            <a href={href} target="_blank" rel="noreferrer" className="text-accent hover:underline">
              {children}
            </a>
          ),
          // See the note above: the alt text stays, the request never happens.
          img: ({ alt }) => (
            <span className="type-support text-ink-secondary italic">{alt || "image"}</span>
          ),
          code: ({ children }) => (
            <code className="type-code bg-muted text-ink rounded-inner px-1.5 py-0.5">
              {children}
            </code>
          ),
          pre: ({ children }) => (
            <pre className="type-code bg-surface border-border rounded-element overflow-x-auto border p-3">
              {children}
            </pre>
          ),
          blockquote: ({ children }) => (
            <blockquote className="border-border-emphasized text-ink-secondary border-s-2 ps-3">
              {children}
            </blockquote>
          ),
          // Wide tables scroll inside their own container rather than pushing
          // the conversation sideways.
          table: ({ children }) => (
            <div className="border-border rounded-element overflow-x-auto border">
              <table className="w-full border-collapse">{children}</table>
            </div>
          ),
          thead: ({ children }) => <thead className="bg-surface">{children}</thead>,
          th: ({ children }) => (
            <th className="type-label text-ink-secondary border-border border-b px-3 py-2 text-start whitespace-nowrap">
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className="type-support text-ink border-border border-b px-3 py-2 tabular-nums last:border-0">
              {children}
            </td>
          ),
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}
